import { createGuidedVisualGuidance } from '@/routes/guided-recording/media/GuidedVisualGuidance';

const INITIALIZATION_TIMEOUT_MS = 15000;
const INFERENCE_TIMEOUT_MS = 2000;
const SAMPLE_FRESHNESS_MS = 1000;
const createTaskState = () => ({
  worker: null,
  state: 'idle',
  reason: null,
  timer: null,
  inFlight: false,
  timestampMs: -Infinity,
  lastSample: -Infinity,
  processed: false,
});

export const createGuidedVideoPipeline = ({ sourceVideo, blurEnabled = false, onGuidance, onCapability }) => {
  let disposed = false;
  let generation = 1;
  const tasks = { face: createTaskState(), segmentation: createTaskState() };
  let frame;
  let lastFrame = -Infinity;
  let lastVideoTime = -Infinity;
  let lastMaskAt = -Infinity;
  let lastFaceAt = -Infinity;
  let blurRequested = Boolean(blurEnabled);
  let blurFailed = false;
  let face = null;
  let framingState = 'loading';
  let blurState = 'off';
  let canvasStream;
  let composedStream;
  let maskValues;
  let maskImage;
  let recording = false;
  let faceHint = null;
  let started = false;
  const guidance = createGuidedVisualGuidance();
  const canvas = document.createElement('canvas');
  const foreground = document.createElement('canvas');
  const maskCanvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  const foregroundContext = foreground.getContext('2d');
  const maskContext = maskCanvas.getContext('2d');
  const compositingSupported = Boolean(context && foregroundContext && maskContext
    && typeof context.filter === 'string' && canvas.captureStream);
  const visionSupported = Boolean(window.Worker && window.createImageBitmap && window.OffscreenCanvas && window.WebAssembly);
  if (!visionSupported) framingState = 'unavailable';
  const capability = () => onCapability?.({
    blurSupported: compositingSupported && visionSupported && !blurFailed,
    blurState,
    guidanceSupported: visionSupported && tasks.face.state === 'ready',
    visionState: !visionSupported ? 'unsupported'
      : tasks.face.state === 'failed' ? 'failed' : tasks.face.state === 'ready' ? 'ready' : 'loading',
    visionReason: !visionSupported ? 'visionUnsupported' : tasks.face.reason,
    blurReason: !compositingSupported ? 'blurCompositingUnsupported'
      : !visionSupported ? 'visionUnsupported' : tasks.segmentation.reason,
  });
  const hint = () => onGuidance?.({
    visual: faceHint,
    capability: blurState === 'stopped' ? 'backgroundBlurStopped'
      : blurState === 'unavailable' ? 'backgroundBlurUnavailable'
        : tasks.face.state === 'failed' || !visionSupported ? 'visionUnavailable' : null,
    face,
    framingState,
  });
  const failBlur = (reason) => {
    blurFailed = true;
    tasks.segmentation.reason = reason;
    window.clearTimeout(tasks.segmentation.timer);
    tasks.segmentation.worker?.terminate();
    tasks.segmentation.worker = null;
    tasks.segmentation.inFlight = false;
    tasks.segmentation.state = 'failed';
    blurState = recording && canvasStream ? 'stopped' : 'unavailable';
    capability();
    hint();
  };
  const failTask = (name, reason) => {
    if (disposed) return;
    const entry = tasks[name];
    window.clearTimeout(entry.timer);
    entry.worker?.terminate();
    entry.worker = null;
    entry.inFlight = false;
    entry.state = 'failed';
    entry.reason = reason;
    if (name === 'face') {
      face = null;
      faceHint = null;
      framingState = 'unavailable';
    } else {
      blurFailed = true;
      if (blurRequested) failBlur(reason);
    }
    capability();
    hint();
  };
  const updateMask = (mask) => {
    const values = new Float32Array(mask.buffer);
    if (!maskValues || maskValues.length !== values.length
      || maskCanvas.width !== mask.width || maskCanvas.height !== mask.height) {
      maskCanvas.width = mask.width;
      maskCanvas.height = mask.height;
      maskValues = new Float32Array(values);
      maskImage = maskContext.createImageData(mask.width, mask.height);
    } else {
      for (let index = 0; index < values.length; index += 1) maskValues[index] += 0.5 * (values[index] - maskValues[index]);
    }
    for (let index = 0; index < values.length; index += 1) {
      const alpha = Math.max(0, Math.min(1, (maskValues[index] - 0.45) / 0.20));
      maskImage.data[index * 4 + 3] = Math.round(alpha * alpha * (3 - 2 * alpha) * 255);
    }
    maskContext.putImageData(maskImage, 0, 0);
    lastMaskAt = performance.now();
    if (blurRequested && !blurFailed && blurState !== 'active') {
      blurState = 'active';
      try {
        ensureComposedStream(sourceVideo.srcObject?.getAudioTracks()[0]);
        capability();
      } catch { failBlur('blurCaptureFailed'); }
    }
  };
  const resize = () => {
    const width = sourceVideo.videoWidth;
    const height = sourceVideo.videoHeight;
    if (!width || !height) return false;
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      foreground.width = width;
      foreground.height = height;
    }
    return true;
  };
  const draw = () => {
    if (!compositingSupported || !resize()) return;
    const { width, height } = canvas;
    context.globalCompositeOperation = 'source-over';
    context.filter = 'none';
    if (blurState === 'active' && maskImage && performance.now() - lastMaskAt <= SAMPLE_FRESHNESS_MS) {
      context.filter = `blur(${16 * height / 720}px)`;
      // Expand only the blurred background to avoid a transparent blur edge.
      const padding = 32 * height / 720;
      context.drawImage(sourceVideo, -padding, -padding, width + padding * 2, height + padding * 2);
      context.filter = 'none';
      foregroundContext.globalCompositeOperation = 'source-over';
      foregroundContext.clearRect(0, 0, width, height);
      foregroundContext.drawImage(sourceVideo, 0, 0, width, height);
      foregroundContext.globalCompositeOperation = 'destination-in';
      foregroundContext.drawImage(maskCanvas, 0, 0, width, height);
      foregroundContext.globalCompositeOperation = 'source-over';
      context.drawImage(foreground, 0, 0);
    } else {
      // Preserve this canvas track on inference loss mid-take. Never swap recorder tracks.
      context.drawImage(sourceVideo, 0, 0, width, height);
    }
  };
  const ensureComposedStream = (sourceAudioTrack) => {
    if (composedStream) return composedStream;
    draw();
    const capturedStream = canvas.captureStream(30);
    try {
      const videoTracks = capturedStream.getVideoTracks();
      if (!videoTracks.length) throw new Error('Canvas capture unavailable');
      const stream = new MediaStream([
        ...videoTracks,
        ...(sourceAudioTrack ? [sourceAudioTrack] : []),
      ]);
      canvasStream = capturedStream;
      composedStream = stream;
      return composedStream;
    } catch (error) {
      capturedStream.getVideoTracks().forEach(track => track.stop());
      throw error;
    }
  };
  const sendFrame = async (name, now) => {
    const entry = tasks[name];
    const interval = name === 'face' ? 250 : 1000 / 15;
    if (entry.state !== 'ready' || entry.inFlight || !entry.worker
      || now - entry.lastSample < interval
      || (name === 'segmentation' && (!blurRequested || blurFailed))) return;
    entry.inFlight = true;
    entry.timestampMs = now;
    const version = generation;
    const activeWorker = entry.worker;
    // First inference can compile/allocate lazily. Its cold budget is separate
    // from both asset initialization and the shorter steady-state watchdog.
    entry.timer = window.setTimeout(() => failTask(name, 'visionTimedOut'),
      entry.processed ? INFERENCE_TIMEOUT_MS : INITIALIZATION_TIMEOUT_MS);
    let bitmap;
    try {
      bitmap = await createImageBitmap(sourceVideo);
      if (disposed || version !== generation || entry.worker !== activeWorker) {
        bitmap.close();
        return;
      }
      entry.lastSample = now;
      activeWorker.postMessage({ type: 'frame', task: name, bitmap, generation: version, timestampMs: now }, [bitmap]);
    } catch {
      bitmap?.close();
      failTask(name, 'visionProcessingFailed');
    }
  };
  const sampleFrame = (now) => {
    if (disposed || !sourceVideo.videoWidth || sourceVideo.readyState < 2) return;
    const mediaTime = sourceVideo.currentTime;
    // rAF can fire repeatedly for one decoded camera frame. VIDEO tasks require
    // strictly advancing timestamps and must not infer the same frame twice.
    if (!Number.isFinite(mediaTime) || mediaTime === lastVideoTime) return;
    lastVideoTime = mediaTime;
    void sendFrame('segmentation', now);
    void sendFrame('face', now);
  };
  const loop = (now) => {
    if (disposed) return;
    frame = requestAnimationFrame(loop);
    if (face && now - lastFaceAt > SAMPLE_FRESHNESS_MS) {
      face = null;
      faceHint = null;
      framingState = 'missing';
      hint();
    }
    if (blurState === 'active' && now - lastMaskAt > SAMPLE_FRESHNESS_MS
      && !tasks.segmentation.inFlight) failTask('segmentation', 'visionTimedOut');
    if (!sourceVideo.videoWidth || sourceVideo.readyState < 2 || now - lastFrame < 1000 / 30) return;
    lastFrame = now;
    try { draw(); } catch {
      if (blurRequested && !blurFailed) failBlur('blurCompositingUnsupported');
    }
    // The source video is detached; compositor-driven video callbacks may not
    // arrive. Sample advancing media time from the bounded animation loop.
    sampleFrame(now);
  };
  const startTask = (name) => {
    const entry = tasks[name];
    if (disposed || !visionSupported || entry.state !== 'idle') return;
    entry.state = 'loading';
    entry.timer = window.setTimeout(() => failTask(name, 'visionTimedOut'), INITIALIZATION_TIMEOUT_MS);
    try {
      const taskWorker = new Worker(new URL('./GuidedVisionWorker.js', import.meta.url), { type: 'module' });
      entry.worker = taskWorker;
      taskWorker.onmessage = ({ data }) => {
        if (disposed || entry.worker !== taskWorker || data.generation !== generation || data.task !== name) return;
        if (data.type === 'status') {
          if (data.state === 'failed') {
            failTask(name, data.reason);
            return;
          }
          if (data.state === 'ready') {
            window.clearTimeout(entry.timer);
            entry.state = 'ready';
          }
          capability();
          hint();
          return;
        }
        if (data.type !== 'result' || !entry.inFlight || data.timestampMs !== entry.timestampMs) return;
        window.clearTimeout(entry.timer);
        entry.inFlight = false;
        entry.processed = true;
        if (name === 'face' && data.faceSampled) {
          face = data.face || null;
          lastFaceAt = performance.now();
          framingState = face ? 'tracking' : 'missing';
          faceHint = guidance.update(face, lastFaceAt);
        }
        if (name === 'segmentation' && data.mask && blurRequested && compositingSupported && !blurFailed) {
          try { updateMask(data.mask); } catch { failTask(name, 'visionProcessingFailed'); }
        }
        capability();
        hint();
      };
      taskWorker.onerror = (event) => {
        event.preventDefault?.();
        failTask(name, 'visionWorkerFailed');
      };
      taskWorker.onmessageerror = () => failTask(name, 'visionWorkerFailed');
      taskWorker.postMessage({ type: 'initialize', task: name, generation });
    } catch { failTask(name, 'visionWorkerFailed'); }
  };
  const setBlurEnabled = (enabled) => {
    if (disposed || recording) return;
    blurRequested = Boolean(enabled);
    if (!blurRequested) blurState = 'off';
    else if (!compositingSupported || !visionSupported || blurFailed) blurState = 'unavailable';
    else {
      blurState = 'warming';
      if (started) startTask('segmentation');
    }
    capability();
    hint();
  };
  return {
    async start() {
      if (disposed || started) return;
      started = true;
      setBlurEnabled(blurRequested);
      startTask('face');
      frame = requestAnimationFrame(loop);
      capability();
      hint();
    },
    setBlurEnabled,
    setRecording(value) { recording = Boolean(value); },
    getRecordingStream(sourceAudioTrack) {
      if (disposed) return null;
      if (blurState !== 'active' && !(recording && canvasStream)) return sourceVideo.srcObject;
      try {
        return ensureComposedStream(sourceAudioTrack);
      } catch {
        failBlur('blurCaptureFailed');
        return sourceVideo.srcObject;
      }
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      generation += 1;
      cancelAnimationFrame(frame);
      face = null;
      faceHint = null;
      framingState = 'unavailable';
      hint();
      Object.values(tasks).forEach((entry) => {
        window.clearTimeout(entry.timer);
        const retiringWorker = entry.worker;
        entry.worker = null;
        if (!retiringWorker) return;
        const termination = window.setTimeout(() => retiringWorker.terminate(), 500);
        retiringWorker.onmessage = ({ data }) => {
          if (data.type !== 'disposed') return;
          window.clearTimeout(termination);
          retiringWorker.terminate();
        };
        retiringWorker.postMessage({ type: 'dispose' });
      });
      canvasStream?.getVideoTracks().forEach(track => track.stop());
      canvasStream = null;
      composedStream = null;
      maskValues = null;
      maskImage = null;
      canvas.width = 0;
      canvas.height = 0;
      foreground.width = 0;
      foreground.height = 0;
      maskCanvas.width = 0;
      maskCanvas.height = 0;
    },
  };
};
